"use client";

import { ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Capabilities() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const numeral = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const features = [{
    index: "01",
    eyebrow: "Precision",
    title: "Industrial Laser",
    titleAccent: "Engraving",
    body: "High-speed fiber laser systems deliver permanent, high-contrast markings on industrial components. Our precision-calibrated equipment ensures exact tolerances for serial numbers, barcodes, and complex schematics on any metal surface.",
    cta: "Explore Engraving",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_metal.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_metal.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/laser_engraving_metal.png-wvc-sizes",
      alt: "Industrial fiber laser engraving a stainless steel component with precision"
    },
    imageLeft: true
  }, {
    index: "02",
    eyebrow: "Compliance",
    title: "Arc Flash",
    titleAccent: "Safety",
    body: "Engineered for durability in harsh environments, our arc flash labels meet all NFPA 70E requirements. We provide custom-sized, UV-resistant, and chemical-proof signage that ensures your facility remains compliant and safe.",
    cta: "View Safety Labels",
    image: {
      src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_label_safety.png",
      srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_label_safety.png-wvc-srcset",
      sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_label_safety.png-wvc-sizes",
      alt: "High-visibility arc flash warning label mounted on industrial electrical equipment"
    },
    imageLeft: false
  }, {
    index: "03",
    eyebrow: "Materials",
    title: "Custom Phenolic",
    titleAccent: "Solutions",
    body: "From control panels to equipment tags, our multi-layer phenolic engraving stock offers superior resistance to heat and corrosion. We deliver custom-cut, precision-engraved solutions tailored to your specific industrial specifications.",
    cta: "Review Materials",
    image: {
      src: "https://evermarkindustrial.com/wp-content/uploads/2026/06/Phenolic_Legend_Plates_2-scaled.jpg?v=48a8ru2hmig",
      srcset: "https://evermarkindustrial.com/wp-content/uploads/2026/06/Phenolic_Legend_Plates_2-scaled.jpg?v=6v4rlf36xj5",
      sizes: "https://evermarkindustrial.com/wp-content/uploads/2026/06/Phenolic_Legend_Plates_2-scaled.jpg?v=mdhdb0p8mu",
      alt: "Precision laser‑engraved phenolic industrial label and legend plates"
    },
    imageLeft: true
  }];
  return <section data-section-id="1697" id="capabilities" className="bg-background text-foreground relative overflow-hidden py-28 md:py-40">
      {}
      <div ref={numeral.ref} aria-hidden="true" className="pointer-events-none absolute left-1/2 top-24 -translate-x-1/2 select-none" style={{
      transform: numeral.isVisible ? "translate(-50%, -40px)" : "translate(-50%, 0)",
      transition: "transform 1200ms cubic-bezier(0.22, 0.61, 0.36, 1)"
    }}>
        <span className="font-serif italic leading-none text-transparent" style={{
        fontSize: "clamp(8rem, 16vw, 14rem)",
        WebkitTextStroke: "1px var(--chart-1)",
        opacity: 0.18
      }}>
          02
        </span>
      </div>

      <div className="relative mx-auto w-full max-w-[1320px] px-5 sm:px-8 md:px-14">
        {}
        <div ref={header.ref} className="relative z-10 mx-auto flex max-w-[720px] flex-col items-center text-center" style={{
        opacity: header.isVisible ? 1 : 0,
        transform: header.isVisible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 720ms cubic-bezier(0.22, 0.61, 0.36, 1), transform 720ms cubic-bezier(0.22, 0.61, 0.36, 1)"
      }}>
          {}
          <div className="flex items-center gap-3">
            <span className="font-mono uppercase" style={{
            fontSize: "clamp(0.6875rem, 0.75vw, 0.8125rem)",
            letterSpacing: "0.18em",
            color: "var(--foreground)",
            fontWeight: 500
          }}>
              § N° 02 — Industrial Capabilities
            </span>
          </div>
          <div className="mt-4 flex items-center gap-2">
            <span className="block h-[4px] w-[4px] rounded-sm" style={{
            background: "var(--chart-1)"
          }} />
            <span className="block h-px w-14" style={{
            background: "var(--chart-1)"
          }} />
          </div>

          {}
          <h2 className="font-default mt-8" style={{
          fontFamily: "var(--font-serif)",
          fontSize: "clamp(2.25rem, 4.6vw, 4.25rem)",
          fontWeight: 400,
          letterSpacing: "-0.02em",
          lineHeight: 0.98
        }}>
            Precision engineered for{" "}
            <em className="not-italic" style={{
            fontStyle: "italic",
            color: "var(--chart-1)",
            fontWeight: 400
          }}>
              industry
            </em>
            .
          </h2>

          <p className="font-default mt-6 max-w-[560px]" style={{
          color: "var(--muted-foreground)",
          fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
          lineHeight: 1.55,
          fontWeight: 380
        }}>
            Three core pillars of manufacturing excellence. Every component we produce is built to withstand the most demanding industrial environments.
          </p>
        </div>

        {}
        <div className="mt-20 flex flex-col gap-24 md:mt-28 md:gap-32">
          {features.map((feature, i) => <FeatureRow key={feature.index} data-index={i} feature={feature} delay={i * 80} />)}
        </div>

        {}
        <div className="mt-24 flex items-center justify-start">
          <span className="font-mono" style={{
          fontSize: "11px",
          color: "var(--muted-foreground)",
          letterSpacing: "0.08em"
        }}>Certified Manufacturing Standards</span>
        </div>
      </div>
    </section>;
}
type FeatureProps = {
  feature: {
    index: string;
    eyebrow: string;
    title: string;
    titleAccent: string;
    body: string;
    cta: string;
    image: {
      src: string;
      srcset: string;
      sizes: string;
      alt: string;
    };
    imageLeft: boolean;
  };
  delay: number;
  "data-index"?: number;
};
function FeatureRow({
  feature,
  delay,
  ...rest
}: FeatureProps) {
  const row = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={row.ref} data-index={rest["data-index"]} className="grid grid-cols-1 items-center gap-10 md:grid-cols-12 md:gap-12" style={{
    opacity: row.isVisible ? 1 : 0,
    transform: row.isVisible ? "translateY(0)" : "translateY(16px)",
    transition: `opacity 720ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms, transform 720ms cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms`
  }}>
      {}
      <div className={`md:col-span-7 ${feature.imageLeft ? "md:order-1" : "md:order-2"}`}>
        <div className="group relative">
          {}
          <div className="relative overflow-hidden rounded-lg border transition-all duration-300 shadow-lg" style={{
          background: "var(--card)",
          borderColor: "var(--border)"
        }}>
            <div className="aspect-[4/5] w-full overflow-hidden md:aspect-[5/4]">
              <img src={feature.image.src} data-wvc-srcset={feature.image.srcset} data-wvc-sizes={feature.image.sizes} alt={feature.image.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]" />
            </div>

            {}
            <span className="pointer-events-none absolute left-3 top-3 font-mono uppercase" style={{
            fontSize: "10px",
            color: "var(--chart-1)",
            letterSpacing: "0.08em",
            opacity: 0.7
          }}>
              Precision
            </span>
            <span className="pointer-events-none absolute right-3 top-3 font-mono uppercase" style={{
            fontSize: "10px",
            color: "var(--chart-1)",
            letterSpacing: "0.08em",
            opacity: 0.7
          }}>
              Industrial
            </span>
            <span className="pointer-events-none absolute bottom-3 left-3 font-mono uppercase" style={{
            fontSize: "10px",
            color: "var(--chart-1)",
            letterSpacing: "0.08em",
            opacity: 0.7
          }}>
              § {feature.index}
            </span>
            <span className="pointer-events-none absolute bottom-3 right-3 font-mono uppercase" style={{
            fontSize: "10px",
            color: "var(--chart-1)",
            letterSpacing: "0.08em",
            opacity: 0.7
          }}>
              Manufacturing
            </span>
          </div>
        </div>
      </div>

      {}
      <div className={`md:col-span-5 ${feature.imageLeft ? "md:order-2" : "md:order-1"}`}>
        {}
        <div className="flex items-center gap-3">
          <span className="font-mono uppercase" style={{
          fontSize: "11px",
          letterSpacing: "0.18em",
          color: "var(--foreground)",
          fontWeight: 500
        }}>
            § N° {feature.index} — {feature.eyebrow}
          </span>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <span className="block h-[4px] w-[4px] rounded-sm" style={{
          background: "var(--chart-1)"
        }} />
          <span className="block h-px w-14" style={{
          background: "var(--chart-1)"
        }} />
        </div>

        {}
        <h3 className="font-default mt-6" style={{
        fontFamily: "var(--font-default)",
        fontSize: "clamp(1.5rem, 2.2vw, 2.125rem)",
        fontWeight: 540,
        letterSpacing: "-0.01em",
        lineHeight: 1.12,
        color: "var(--foreground)"
      }}>
          {feature.title}{" "}
          <em className="not-italic" style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          color: "var(--chart-1)",
          fontWeight: 400
        }}>
            {feature.titleAccent}
          </em>
        </h3>

        {}
        <p className="font-default mt-5 max-w-[440px]" style={{
        color: "var(--muted-foreground)",
        fontSize: "clamp(0.9375rem, 1vw, 1rem)",
        lineHeight: 1.62,
        fontWeight: 400
      }}>
          {feature.body}
        </p>

        {}
        <div className="mt-8">
          <Link to={rest["data-index"] === 0 ? "/LaserEngravingMetal2Page" : rest["data-index"] === 1 ? "/ArcFlashLabelsPage" : rest["data-index"] === 2 ? "/Phenolic" : "#"}>
            <Button variant="link" className="group/btn h-auto rounded-md p-0 hover:no-underline" style={{
            color: "var(--foreground)",
            fontFamily: "var(--font-default)",
            fontWeight: 460,
            fontSize: "0.9375rem"
          }}>
              <span className="relative inline-flex items-center gap-2">
                <span className="relative">
                  {feature.cta}
                  <span className="absolute -bottom-1.5 left-0 block h-px w-full origin-left" style={{
                  background: "var(--chart-1)",
                  transform: "scaleX(1)",
                  transformOrigin: "left",
                  transition: "transform 280ms cubic-bezier(0.22, 0.61, 0.36, 1)"
                }} />
                </span>
                <ArrowRight className="size-4 transition-transform duration-300 ease-out group-hover/btn:translate-x-1" style={{
                color: "var(--chart-1)"
              }} />
              </span>
            </Button>
          </Link>
        </div>
      </div>
    </div>;
}